"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  MULTIVOLUME_FIELDS as F,
  formatMultivolume,
  type MultivolumeKind,
  type MultivolumeLabels,
  multivolumeIsReady,
} from "@/lib/tool-kit/citation-sources-books/multivolume-work-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const MULTIVOLUME_WORK_CITATION_GENERATOR_STRINGS = {
  formHeading: "Work details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the work's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What are you citing?",
  kindSet: "The whole set",
  kindVolume: "One volume",
  authorsLabel: "Author(s) or editor(s)",
  authorsPlaceholder: "Shelby Foote",
  authorsHint: "One per line, as First Last or Last, First.",
  editorsLabel: "These people are editors, not authors",
  titleLabel: "Title of the work",
  titlePlaceholder: "The Civil War: A narrative",
  titleHint:
    "The title of the whole work, in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  editionLabel: "Edition (optional)",
  editionPlaceholder: "2nd",
  volumesLabel: "Number of volumes in the set",
  volumesPlaceholder: "3",
  volumesHint:
    "The total, even if you used only some. MLA and Chicago print it as 3 vols.; APA prints Vols. 1–3.",
  volumesUsedLabel: "Volumes you used, if not all (APA)",
  volumesUsedPlaceholder: "2–3",
  volumesUsedHint:
    "APA lists the volumes you used. Leave blank to list them all.",
  volumeSetLabel: "Volume you quote (optional)",
  volumeSetPlaceholder: "2",
  volumeSetHint:
    "Adds the volume to the in-text citations: (Foote 2: 321) in MLA, 2:321 in a Chicago note.",
  volumeLabel: "Volume number",
  volumePlaceholder: "3",
  volumeHint: "Roman numerals are changed to Arabic: III becomes 3.",
  volumeTitleLabel: "Title of the volume (if it has its own)",
  volumeTitlePlaceholder: "Master of the Senate",
  volumeTitleHint:
    "Leave blank when the volume is only numbered. Sentence case, as for the title.",
  yearLabel: "Year published (or first year)",
  yearEndLabel: "Last year (if the set came out over several years)",
  yearPlaceholder: "1958",
  yearEndPlaceholder: "1974",
  publisherLabel: "Publisher",
  publisherPlaceholder: "Random House",
  pageLabel: "Page or pages you quote (optional)",
  pagePlaceholder: "321",
  pageHint: "Goes into the in-text citations and the Chicago notes.",
  shortTitleLabel: "Short title for notes (Chicago, optional)",
  shortTitlePlaceholder: "Civil War",
  shortTitleHint:
    "Chicago shortens a long title to its key words, by hand. Leave blank for a suggestion.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 puts Vol. or Vols. in parentheses after the italic title and gives a set published over several years a range of years. A volume with its own title carries its number and title inside the italics, after a colon.",
  mlaNote:
    "MLA 9 prints the total number of volumes at the end of a set's entry, and the volume number as an element after the title for a single volume. In text, volume and page are divided by a colon. A volume with its own title is listed under that title; MLA lets you leave out the set, and this adds it so readers can find it.",
  chicagoNote:
    "Chicago 18 gives a set's volume count after the title, and a volume in a note as vol. 2 before the publisher. A volume and page together read 2:321. For a volume with its own title the shortened note uses that title, a convention rather than a published example.",
  exampleSet: {
    authors: "Shelby Foote",
    title: "The Civil War: A narrative",
    volumes: "3",
    year: "1958",
    yearEnd: "1974",
    publisher: "Random House",
    volume: "2",
    page: "321",
    shortTitle: "Civil War",
  },
  exampleVolume: {
    authors: "Robert A. Caro",
    title: "The years of Lyndon Johnson",
    volumeTitle: "Master of the Senate",
    volume: "3",
    year: "2002",
    publisher: "Alfred A. Knopf",
  },
} as const;

type Strings = typeof MULTIVOLUME_WORK_CITATION_GENERATOR_STRINGS;

function textField(
  id: string,
  label: string,
  placeholder: string,
  hint?: string
): SourceField {
  return { id, kind: "text", label, placeholder, hint };
}

function leadFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    { id: F.editors, kind: "checkbox", label: strings.editorsLabel },
    textField(
      F.title,
      strings.titleLabel,
      strings.titlePlaceholder,
      strings.titleHint
    ),
    textField(F.edition, strings.editionLabel, strings.editionPlaceholder),
  ];
}

function kindFields(strings: Strings, kind: MultivolumeKind): SourceField[] {
  if (kind === "set") {
    return [
      textField(
        F.volumes,
        strings.volumesLabel,
        strings.volumesPlaceholder,
        strings.volumesHint
      ),
      textField(
        F.volumesUsed,
        strings.volumesUsedLabel,
        strings.volumesUsedPlaceholder,
        strings.volumesUsedHint
      ),
      textField(
        F.volume,
        strings.volumeSetLabel,
        strings.volumeSetPlaceholder,
        strings.volumeSetHint
      ),
    ];
  }
  return [
    textField(
      F.volume,
      strings.volumeLabel,
      strings.volumePlaceholder,
      strings.volumeHint
    ),
    textField(
      F.volumeTitle,
      strings.volumeTitleLabel,
      strings.volumeTitlePlaceholder,
      strings.volumeTitleHint
    ),
  ];
}

function tailFields(strings: Strings, kind: MultivolumeKind): SourceField[] {
  const years: SourceField[] = [
    {
      id: F.year,
      kind: "date",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
  ];
  if (kind === "set") {
    years.push({
      id: F.yearEnd,
      kind: "date",
      label: strings.yearEndLabel,
      placeholder: strings.yearEndPlaceholder,
    });
  }
  return [
    ...years,
    textField(
      F.publisher,
      strings.publisherLabel,
      strings.publisherPlaceholder
    ),
    textField(
      F.page,
      strings.pageLabel,
      strings.pagePlaceholder,
      strings.pageHint
    ),
    textField(
      F.shortTitle,
      strings.shortTitleLabel,
      strings.shortTitlePlaceholder,
      strings.shortTitleHint
    ),
  ];
}

/** The widget's strings, as the formatter's labels. */
export function multivolumeLabels(strings: Strings): MultivolumeLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function MultivolumeWorkCitationGeneratorWidget() {
  const strings = useToolUi(MULTIVOLUME_WORK_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<MultivolumeKind>("set");
  const fields = useMemo(
    () => [
      ...leadFields(strings),
      ...kindFields(strings, kind),
      ...tailFields(strings, kind),
    ],
    [strings, kind]
  );
  const labels = useMemo(() => multivolumeLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatMultivolume(kind, values, labels),
    [kind, labels]
  );

  return (
    <SourceCitationBuilder
      example={kind === "set" ? strings.exampleSet : strings.exampleVolume}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="multivolume-kind"
          onChange={setKind}
          options={[
            { value: "set", label: strings.kindSet },
            { value: "volume", label: strings.kindVolume },
          ]}
          value={kind}
        />
      }
      isReady={multivolumeIsReady}
      strings={strings}
    />
  );
}
